import { useEffect, useMemo, useState } from "react";
import { Loader2, MapPin, Plus, X, Briefcase, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const COVERAGE_TYPES = [
  { value: "national", label: "Nationwide (entire US)" },
  { value: "state", label: "By state(s)" },
  { value: "county", label: "By county(s)" },
  { value: "city", label: "By city(s)" },
  { value: "zip", label: "By ZIP code(s)" },
  { value: "radius", label: "Radius from office(s)" },
];

function Section({ title, children, sub }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
      <header className="mb-4">
        <h2 className="font-heading text-lg font-semibold text-[#0A192F]">{title}</h2>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </header>
      {children}
    </section>
  );
}

function Pill({ label, onRemove, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs"
    >
      {label}
      {onRemove && (
        <button onClick={onRemove} className="hover:text-red-600">
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

export default function TerritoryBuilder() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coverage, setCoverage] = useState("national");
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState([]);
  const [zipCodes, setZipCodes] = useState([]);
  const [offices, setOffices] = useState([]);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [acceptingNew, setAcceptingNew] = useState(true);
  const [minAiConfidence, setMinAiConfidence] = useState(0);
  const [maxNewPerMonth, setMaxNewPerMonth] = useState("");

  const [allStates, setAllStates] = useState([]);
  const [allPracticeAreas, setAllPracticeAreas] = useState([]);
  const [selectedStateForCounty, setSelectedStateForCounty] = useState("CA");
  const [stateCounties, setStateCounties] = useState([]);
  const [stateCities, setStateCities] = useState([]);
  const [zipInput, setZipInput] = useState("");
  const [officeInput, setOfficeInput] = useState({ label: "", address: "", radius_miles: 25 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, p, t] = await Promise.all([
          api.get("/geo/states"),
          api.get("/practice-areas"),
          api.get("/territory"),
        ]);
        if (!alive) return;
        setAllStates(s.data?.states || []);
        setAllPracticeAreas(p.data?.practice_areas || []);
        const ter = t.data || {};
        setCoverage(ter.coverage_type || "national");
        setStates(ter.states || []);
        setCounties(ter.counties || []);
        setCities(ter.cities || []);
        setZipCodes(ter.zip_codes || []);
        setOffices(ter.offices || []);
        setPracticeAreas(ter.practice_areas || []);
        const prefs = ter.preferences || {};
        setAcceptingNew(prefs.accepting_new_cases !== false);
        setMinAiConfidence(prefs.min_ai_confidence || 0);
        setMaxNewPerMonth(prefs.max_new_cases_per_month || "");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedStateForCounty) return;
    let alive = true;
    Promise.all([
      api.get(`/geo/counties?state=${selectedStateForCounty}`),
      api.get(`/geo/cities?state=${selectedStateForCounty}`),
    ]).then(([cn, ct]) => {
      if (!alive) return;
      setStateCounties(cn.data?.counties || []);
      setStateCities(ct.data?.cities || []);
    });
    return () => {
      alive = false;
    };
  }, [selectedStateForCounty]);

  const groupedAreas = useMemo(() => {
    const out = {};
    allPracticeAreas.forEach((p) => {
      if (!out[p.category]) out[p.category] = [];
      out[p.category].push(p);
    });
    return out;
  }, [allPracticeAreas]);

  const toggleArr = (arr, setter, value) => {
    if (arr.includes(value)) setter(arr.filter((v) => v !== value));
    else setter([...arr, value]);
  };

  const addZip = () => {
    const digits = zipInput.replace(/\D/g, "").slice(0, 5);
    if (digits.length === 5 && !zipCodes.includes(digits)) {
      setZipCodes([...zipCodes, digits]);
    }
    setZipInput("");
  };

  const addOffice = () => {
    if (!officeInput.address) return;
    setOffices([...offices, { ...officeInput }]);
    setOfficeInput({ label: "", address: "", radius_miles: 25 });
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        coverage_type: coverage,
        states,
        counties,
        cities,
        zip_codes: zipCodes,
        offices,
        practice_areas: practiceAreas,
        preferences: {
          accepting_new_cases: acceptingNew,
          min_ai_confidence: Number(minAiConfidence) || 0,
          max_new_cases_per_month: maxNewPerMonth ? Number(maxNewPerMonth) : null,
          languages: ["en"],
        },
      };
      const r = await api.put("/territory", body);
      toast.success("Territory saved");
      // Update with normalized server response.
      setStates(r.data.states || []);
      setZipCodes(r.data.zip_codes || []);
      setOffices(r.data.offices || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save territory");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#0A192F]" />
      </div>
    );
  }

  return (
    <div data-testid="territory-builder" className="p-6 lg:p-10 max-w-6xl">
      <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-2">
        Service area
      </p>
      <h1 className="font-heading text-3xl font-bold text-[#0A192F] mb-8">
        Territory & Practice Areas
      </h1>

      <Section title="Coverage type" sub="Choose how broadly you accept cases.">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {COVERAGE_TYPES.map((c) => (
            <button
              key={c.value}
              data-testid={`coverage-${c.value}`}
              onClick={() => setCoverage(c.value)}
              className={`text-left p-3 rounded-lg border transition ${
                coverage === c.value
                  ? "border-[#C5A059] bg-amber-50 text-[#0A192F]"
                  : "border-slate-200 hover:border-slate-300 text-slate-700"
              }`}
            >
              <p className="text-sm font-semibold">{c.label}</p>
            </button>
          ))}
        </div>
      </Section>

      {(coverage === "state" || coverage === "county" || coverage === "city") && (
        <Section
          title="States covered"
          sub="Pick the states where you accept cases."
        >
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {allStates.map((s) => (
              <button
                key={s.code}
                data-testid={`state-${s.code}`}
                onClick={() => toggleArr(states, setStates, s.code)}
                className={`text-xs p-2 rounded border transition ${
                  states.includes(s.code)
                    ? "border-[#0A192F] bg-[#0A192F] text-white"
                    : "border-slate-200 hover:border-slate-400 text-slate-700"
                }`}
              >
                {s.code}
              </button>
            ))}
          </div>
        </Section>
      )}

      {(coverage === "county" || coverage === "city") && states.length > 0 && (
        <Section title="Counties / Cities" sub="Drill into specific counties or cities within your states.">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Browse state:</span>
            <select
              value={selectedStateForCounty}
              onChange={(e) => setSelectedStateForCounty(e.target.value)}
              className="border border-slate-200 rounded px-2 py-1 text-sm"
              data-testid="county-state-picker"
            >
              {states.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          {coverage === "county" && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 mb-4 max-h-48 overflow-auto">
              {stateCounties.map((c) => {
                const key = `${c.state}:${c.name}`;
                const on = counties.includes(key);
                return (
                  <button
                    key={key}
                    data-testid={`county-${c.name}`}
                    onClick={() => toggleArr(counties, setCounties, key)}
                    className={`text-xs p-2 rounded border text-left ${
                      on ? "border-[#0A192F] bg-[#0A192F] text-white" : "border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          )}
          {coverage === "city" && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 max-h-48 overflow-auto">
              {stateCities.map((c) => {
                const key = `${c.state}:${c.name}`;
                const on = cities.includes(key);
                return (
                  <button
                    key={key}
                    data-testid={`city-${c.name}`}
                    onClick={() => toggleArr(cities, setCities, key)}
                    className={`text-xs p-2 rounded border text-left ${
                      on ? "border-[#0A192F] bg-[#0A192F] text-white" : "border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          )}
        </Section>
      )}

      {coverage === "zip" && (
        <Section title="ZIP codes" sub="Add specific ZIP codes. We accept 5-digit US ZIPs.">
          <div className="flex gap-2 mb-3">
            <Input
              data-testid="zip-input"
              value={zipInput}
              onChange={(e) => setZipInput(e.target.value)}
              placeholder="90210"
              onKeyDown={(e) => e.key === "Enter" && addZip()}
              className="w-32"
            />
            <Button onClick={addZip} variant="outline" size="sm" data-testid="zip-add">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {zipCodes.map((z) => (
              <Pill
                key={z}
                label={z}
                testId={`zip-pill-${z}`}
                onRemove={() => setZipCodes(zipCodes.filter((x) => x !== z))}
              />
            ))}
          </div>
        </Section>
      )}

      {coverage === "radius" && (
        <Section title="Office locations" sub="Each office matches cases within its radius.">
          <div className="space-y-3 mb-4">
            {offices.map((o, i) => (
              <div key={i} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#C5A059]" />
                  <div>
                    <p className="text-sm font-semibold text-[#0A192F]">{o.label || o.address}</p>
                    <p className="text-xs text-slate-500">
                      {o.address}
                      {o.lat && o.lng && ` · ${o.lat.toFixed(4)}, ${o.lng.toFixed(4)}`}
                      {" · "}
                      <span className="font-semibold">{o.radius_miles} mi radius</span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setOffices(offices.filter((_, j) => j !== i))}
                  className="text-slate-400 hover:text-red-600"
                  data-testid={`office-remove-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            <Input
              data-testid="office-label-input"
              placeholder="Office label (e.g. LA HQ)"
              value={officeInput.label}
              onChange={(e) => setOfficeInput({ ...officeInput, label: e.target.value })}
            />
            <Input
              data-testid="office-address-input"
              placeholder="Full address"
              value={officeInput.address}
              onChange={(e) => setOfficeInput({ ...officeInput, address: e.target.value })}
            />
            <select
              data-testid="office-radius-input"
              value={officeInput.radius_miles}
              onChange={(e) =>
                setOfficeInput({ ...officeInput, radius_miles: Number(e.target.value) })
              }
              className="border border-slate-200 rounded px-2 py-1 text-sm"
            >
              {[10, 25, 50, 100, 250].map((r) => (
                <option key={r} value={r}>{r} miles</option>
              ))}
            </select>
          </div>
          <Button onClick={addOffice} variant="outline" size="sm" data-testid="office-add">
            <Plus className="w-4 h-4 mr-1" /> Add office
          </Button>
        </Section>
      )}

      <Section title="Practice areas" sub="Only cases matching one of these will be routed to you.">
        {Object.entries(groupedAreas).map(([cat, items]) => (
          <div key={cat} className="mb-4">
            <p className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500 mb-2">{cat}</p>
            <div className="flex flex-wrap gap-2">
              {items.map((p) => {
                const on = practiceAreas.includes(p.code);
                return (
                  <button
                    key={p.code}
                    data-testid={`practice-${p.code}`}
                    onClick={() => toggleArr(practiceAreas, setPracticeAreas, p.code)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      on
                        ? "border-[#0A192F] bg-[#0A192F] text-white"
                        : "border-slate-200 text-slate-700 hover:border-slate-400"
                    }`}
                  >
                    <Briefcase className="w-3 h-3 inline mr-1" />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </Section>

      <Section title="Preferences" sub="Optional — tune how cases are filtered before they reach you.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              data-testid="pref-accepting"
              type="checkbox"
              checked={acceptingNew}
              onChange={(e) => setAcceptingNew(e.target.checked)}
            />
            Currently accepting new cases
          </label>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Min AI confidence</label>
            <Input
              data-testid="pref-min-confidence"
              type="number"
              min={0}
              max={100}
              value={minAiConfidence}
              onChange={(e) => setMinAiConfidence(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Max new cases / month</label>
            <Input
              data-testid="pref-max-month"
              type="number"
              min={0}
              value={maxNewPerMonth}
              onChange={(e) => setMaxNewPerMonth(e.target.value)}
              placeholder="No limit"
            />
          </div>
        </div>
      </Section>

      <div className="flex items-center justify-end gap-3 sticky bottom-0 bg-[#F8FAFC]/95 backdrop-blur py-4">
        <Button
          data-testid="territory-save-btn"
          onClick={save}
          disabled={saving}
          className="bg-[#0A192F] hover:bg-[#0A192F]/90 text-white"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" /> Save territory
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
